// src/services/absenceCalendarService.js
// =============================================================================
// HR Absence Calendar API service
// =============================================================================
//
// PURPOSE
// -------
// Thin frontend service for the HR Absence Calendar read model.
//
// This service intentionally stays small and predictable:
// - it builds query parameters,
// - it calls the central apiClient,
// - it returns the backend DTO without reinterpreting business rules.
//
// IMPORTANT ARCHITECTURAL NOTE
// ----------------------------
// The absence calendar is a read model over existing Leave / HR data.  It is not
// a new workflow module.  Creating/approving/cancelling leave requests remains
// owned by the existing leave request APIs and work-item flow.

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function joinCsv(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(",");
}

function joinNumericCsv(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0)
    .join(",");
}

function buildRequestConfig(options = {}) {
  return {
    withCredentials: true,
    signal: options.signal,
  };
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fetch the absence calendar read model.
 *
 * @param {object} params
 * @param {string} params.from - YYYY-MM-DD inclusive start.
 * @param {string} params.to - YYYY-MM-DD inclusive end.
 * @param {number[]} [params.userIds] - Optional selected employee ids.
 * @param {string[]} [params.statuses] - Optional request statuses.
 * @param {string[]} [params.categories] - Optional UI categories.
 * @param {boolean} [params.includeCancelled=false]
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{range: object, users: object[], events: object[], connections: object[], meta: object}>}
 */
export async function fetchAbsenceCalendar(params = {}, options = {}) {
  try {
    const query = new URLSearchParams();

    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);

    const userIds = joinNumericCsv(params.userIds);
    if (userIds) query.set("user_ids", userIds);

    const statuses = joinCsv(params.statuses);
    if (statuses) query.set("statuses", statuses);

    const categories = joinCsv(params.categories);
    if (categories) query.set("categories", categories);

    if (params.includeCancelled) {
      query.set("include_cancelled", "true");
    }

    const response = await apiClient.get(
      `/hr/absence-calendar?${query.toString()}`,
      buildRequestConfig(options)
    );

    return {
      range: response.data?.range || { from: params.from, to: params.to },
      users: Array.isArray(response.data?.users) ? response.data.users : [],
      events: Array.isArray(response.data?.events) ? response.data.events : [],
      connections: Array.isArray(response.data?.connections) ? response.data.connections : [],
      meta: response.data?.meta || {},
    };
  } catch (error) {
    if (isRequestCanceled(error)) throw error;

    console.error("[absenceCalendarService] fetchAbsenceCalendar failed:", error);
    throw error;
  }
}
